import React, { useEffect, useRef } from 'react';
import { ArrowLeft, ArrowRight, LoaderCircle } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppRedux';
import { useI18n } from '../../hooks/useI18n';
import { sendOtp, setAuthMode, setMobile } from '../../redux/slices/authSlice';
import { Button } from '../../components/ui/Button';

const MOBILE_LENGTH = 11;

export const LoginForm = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const mobileInputRef = useRef(null);
  const { t, language } = useI18n();
  const { authMode, mobile, loading, error, isAuthenticated } = useAppSelector((state) => state.auth);
  const isRegisterMode = authMode === 'register';
  const isValidMobile = mobile.length >= MOBILE_LENGTH;
  const DirectionArrow = language === 'fa' ? ArrowLeft : ArrowRight;

  const onSendOtp = async () => {
    if (!isValidMobile || loading) {
      return;
    }

    const result = await dispatch(sendOtp(mobile));
    if (sendOtp.fulfilled.match(result)) {
      navigate('/login/otp', { state: location.state });
    }
  };

  useEffect(() => {
    mobileInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(location.state?.redirectTo || '/home', {
        replace: true,
        state: location.state?.redirectState || {
          openCreateProject: location.state?.openCreateProject,
          projectDraft: location.state?.projectDraft,
          openPublicTemplateId: location.state?.openPublicTemplateId
        }
      });
    }
  }, [isAuthenticated, location.state, navigate]);

  return (
    <>
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-text">
          {isRegisterMode ? t('registerTitle') : t('loginTitle')}
        </h1>
        <p className="mt-2 text-sm text-muted">
          {isRegisterMode ? t('registerSubtitle') : t('loginSubtitle')}
        </p>
      </header>

      <div
        role="tablist"
        className="mt-8 flex border-b border-border"
        aria-label={t('loginCardHint')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={!isRegisterMode}
          onClick={() => dispatch(setAuthMode('login'))}
          className={`-mb-px flex-1 border-b-2 pb-3 text-sm font-semibold transition ${
            !isRegisterMode ? 'border-accent text-text' : 'border-transparent text-muted hover:text-text'
          }`}
        >
          {t('loginMode')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={isRegisterMode}
          onClick={() => dispatch(setAuthMode('register'))}
          className={`-mb-px flex-1 border-b-2 pb-3 text-sm font-semibold transition ${
            isRegisterMode ? 'border-accent text-text' : 'border-transparent text-muted hover:text-text'
          }`}
        >
          {t('registerMode')}
        </button>
      </div>

      <form
        className="mt-8 space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          onSendOtp();
        }}
      >
        <div className="space-y-2">
          <label htmlFor="auth-mobile" className="text-sm font-medium text-text">
            {t('mobileLabel')}
          </label>
          <input
            id="auth-mobile"
            ref={mobileInputRef}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            dir="ltr"
            maxLength={MOBILE_LENGTH}
            value={mobile}
            placeholder={t('mobilePlaceholder')}
            onChange={(event) => dispatch(setMobile(event.target.value.replace(/\D/g, '').slice(0, MOBILE_LENGTH)))}
            className={`w-full rounded-xl border bg-panel-strong px-4 py-3.5 text-center text-lg font-semibold tracking-wide text-text outline-none transition placeholder:text-muted/50 focus:border-accent focus:ring-2 focus:ring-accent/20 ${
              error ? 'border-rose-400' : 'border-border'
            }`}
            aria-invalid={Boolean(error)}
            aria-describedby="auth-mobile-hint"
          />
          <p id="auth-mobile-hint" className="text-xs text-muted">
            {isRegisterMode ? t('registerInputHint') : t('loginInputHint')}
          </p>
        </div>

        {error ? (
          <p className="text-sm text-rose-600 dark:text-rose-300" role="alert">
            {error}
          </p>
        ) : null}

        <Button
          type="submit"
          className="min-h-12 w-full rounded-xl text-base font-semibold"
          disabled={loading || !isValidMobile}
        >
          {loading ? (
            <>
              <LoaderCircle size={18} className="animate-spin" />
              {t('sendingOtp')}
            </>
          ) : (
            <>
              {isRegisterMode ? t('sendRegisterOtp') : t('sendOtp')}
              <DirectionArrow size={18} />
            </>
          )}
        </Button>
      </form>

      <p className="mt-8 text-center text-sm text-muted">
        <Link to="/" className="inline-flex items-center gap-1 font-medium text-text hover:text-accent">
          {language === 'fa' ? (
            <>
              {t('navHome')}
              <ArrowRight size={14} />
            </>
          ) : (
            <>
              <ArrowLeft size={14} />
              {t('navHome')}
            </>
          )}
        </Link>
      </p>
    </>
  );
};
